import { useMemo } from 'react';
import {
    ProviderCareTeamContactDetails,
    ProviderCareTeamMemberDetails,
} from '../../../../primary-record-types/apiTypes/medicalProfileService';
import { getIntl } from '../../../../utils/intl';
import {
    ContactDetailField,
    nameOrCompanyRequiredCustomValidators,
    useGetRenderContactDetailsFormFields,
} from '../../ContactDetailsForm';
import { ProviderFormValue } from '../types';
import { defaultProviderValues, isDoctorOrSurgeon, providerSpecialtyOptions, providerTypeOptions } from '../utils';
import CareTeamTypeSelect from '../CareTeamTypeSelect';
import { SplitFormRow } from '../../../v2BaseComponents/Form';
import { Field } from 'formik';
import { FormikInputs } from '../../../v2BaseComponents/Inputs';
import { AVATAR_UPLOAD_QUALITY } from '../../../../utils/constants';
import React from 'react';
import { required } from '../../../../utils/validators';
import { FormStack } from '../../../v2BaseComponents/Stack';
import { CareTeamMemberSource, getCareTeamMemberAvatarUrl } from '../../../../utils/careTeamUtils';

const intl = getIntl('ProviderFields', {
    submit: 'Submit',
    photo: 'Photo',
    providerType: 'Provider Type',
    providerSpecialty: 'Provider Specialty',
    isActive: 'Is Active',
    selectProviderType: 'Select Provider Type',
    selectProviderSpecialty: 'Select Provider Specialty',
});

const contactDetailsFields: ContactDetailField[] = [
    'firstName',
    'lastName',
    'company',
    'description',
    'phoneNumber',
    'faxNumber',
    'email',
    'role',
    'address1',
    'address2',
    'city',
    'state',
    'zipCode',
    'url',
];

export const useProviderContactDetailsFields = (isDisabled?: boolean) =>
    useGetRenderContactDetailsFormFields({
        isDisabled: isDisabled,
        fields: contactDetailsFields,
        fieldNamePrefix: 'contactDetails',
        customValidators: nameOrCompanyRequiredCustomValidators,
    });

export const useProviderInitialValues = (
    initialValues?: ProviderCareTeamMemberDetails<ProviderCareTeamContactDetails>,
) =>
    useMemo<ProviderFormValue>(() => {
        if (!initialValues) return defaultProviderValues;

        return {
            ...initialValues,
            avatar: null,
        };
    }, [initialValues]);

export type ProviderFieldsProps = {
    careTeamMemberId?: string;
    isDisabled?: boolean;
    source: CareTeamMemberSource;
    values: ProviderFormValue;
};

const ProviderFields: React.FC<ProviderFieldsProps> = ({ careTeamMemberId, isDisabled, source, values }) => {
    const { renderFields } = useProviderContactDetailsFields(isDisabled);
    return (
        <FormStack>
            <CareTeamTypeSelect value="provider" />
            <SplitFormRow>
                <Field
                    name="metadata.providerType"
                    component={FormikInputs.Select}
                    selectOptions={providerTypeOptions}
                    validate={required}
                    isRequired
                    title={intl('providerType')}
                    placeholder={intl('selectProviderType')}
                    allowEmpty
                />
                {isDoctorOrSurgeon(values.metadata.providerType) ? (
                    <Field
                        name="metadata.providerSpecialty"
                        component={FormikInputs.AutocompleteSelect}
                        selectOptions={providerSpecialtyOptions}
                        title={intl('providerSpecialty')}
                        allowEmpty
                    />
                ) : null}
            </SplitFormRow>
            <Field
                name="avatar"
                component={FormikInputs.TempAndExistingImagePicker}
                existingAvatarUrl={getCareTeamMemberAvatarUrl(source, careTeamMemberId)}
                title={intl('photo')}
                imagePickerOptions={{
                    allowsEditing: true,
                    aspect: [1, 1],
                    quality: AVATAR_UPLOAD_QUALITY,
                }}
                sizeVariant="avatar"
            />
            <SplitFormRow>
                <Field name="metadata.isActive" component={FormikInputs.Checkbox} title={intl('isActive')} />
            </SplitFormRow>
            {renderFields()}
        </FormStack>
    );
};

export default React.memo(ProviderFields);
